/**
 * Password field with show/hide toggle using shadcn components
 */

import React, { forwardRef, useId } from 'react';
import { PasswordInput } from '../../components/ui/password-input';
import { Label } from '../../components/ui/label';
import { cn } from '../../lib/utils';

export interface PasswordFieldProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type'> {
  label?: string;
  error?: boolean;
  helperText?: string;
}

export const PasswordField = forwardRef<HTMLInputElement, PasswordFieldProps>(
  ({ label, error, helperText, className, id, ...props }, ref) => {
    // Fall back to a generated id so the label is always associated with the
    // input (screen readers, label-click focus) without every call site
    // inventing one.
    const generatedId = useId();
    const inputId = id ?? generatedId;

    return (
      <div className="space-y-2">
        {label && <Label htmlFor={inputId}>{label}</Label>}
        <PasswordInput
          ref={ref}
          id={inputId}
          className={cn(error && 'border-destructive', className)}
          {...props}
        />
        {helperText && (
          <p className={cn('text-sm', error ? 'text-destructive' : 'text-muted-foreground')}>
            {helperText}
          </p>
        )}
      </div>
    );
  }
);

PasswordField.displayName = 'PasswordField';
